import React, {useRef, useState} from 'react';
import PropTypes from 'prop-types';
import Modal from './Modal';
import {unpackableArchives, unpackArchivesIn} from '../utils/treeUpload/readArchive';
import './TreeUploadDialog.css';

/**
 * A .zip (or .tar.gz) dropped or picked into the file tree: "Unpack here" puts its files in the archive's folder (then
 * the usual upload review: conflicts, skips, progress), "Keep as a file" uploads the archive itself, as before.
 */
const TreeZipChoice = ({collected, onKeep, onUnpacked, onClose}) => {
  const archives = unpackableArchives(collected);
  const [state, setState] = useState(null); // {phase, done, total} while unpacking
  const [error, setError] = useState(null);
  const abortRef = useRef(null);
  const label = archives.length === 1 ? archives[0].relPath.split('/').pop() : `${archives.length} archives`;

  const unpack = async () => {
    const controller = new AbortController();
    abortRef.current = controller;
    setError(null);
    setState({phase: 'reading', done: 0, total: 0});
    try {
      const next = await unpackArchivesIn(collected, {signal: controller.signal, onProgress: setState});
      onUnpacked(next);
    } catch (err) {
      setState(null);
      if (err?.name !== 'AbortError') { setError(err?.message || 'The archive could not be unpacked.'); }
    } finally {
      abortRef.current = null;
    }
  };

  const percent = state?.total ? Math.round((state.done / state.total) * 100) : 0;

  return (
    <Modal className="tree-upload-modal" isOpen onClose={state ? null : onClose} title={`Unpack ${label}?`} type="custom">
      <div className="tree-upload" data-testid="tree-zip-choice">
        {!state && (
          <div className="tree-upload__summary">
            {archives.length === 1 ? <><span className="tree-upload__path">{label}</span> is an archive.</> : `${label} are among the files.`}
            {' '}Unpack {archives.length === 1 ? 'it' : 'them'} here, or keep {archives.length === 1 ? 'it' : 'them'} as {archives.length === 1 ? 'a file' : 'files'}?
          </div>
        )}
        {state && (
          <>
            <div className="tree-upload__muted">{state.phase === 'unpacking' ? 'Unpacking…' : 'Reading…'}{state.count > 1 ? ` (${state.index} of ${state.count})` : ''}</div>
            <div className="tree-upload__bar" aria-valuemax={100} aria-valuemin={0} aria-valuenow={percent} role="progressbar">
              <div className="tree-upload__bar-fill" style={{width: `${percent}%`}} />
            </div>
          </>
        )}
        {error && <div className="tree-upload__problem" role="alert">{error}</div>}
        <div className="tree-upload__actions">
          {state ? (
            <button className="tree-upload__btn" onClick={() => abortRef.current?.abort()} type="button">Cancel</button>
          ) : (
            <>
              <button className="tree-upload__btn" onClick={onClose} type="button">Cancel</button>
              <button className="tree-upload__btn" data-testid="tree-zip-keep" onClick={onKeep} type="button">Keep as a file</button>
              <button className="tree-upload__btn tree-upload__btn--primary" data-testid="tree-zip-unpack" onClick={unpack} type="button">Unpack here</button>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
};

TreeZipChoice.propTypes = {
  collected: PropTypes.shape({files: PropTypes.array.isRequired}).isRequired,
  onClose: PropTypes.func.isRequired,
  onKeep: PropTypes.func.isRequired,
  onUnpacked: PropTypes.func.isRequired,
};

export default TreeZipChoice;
